import type { BaseMetadata, ThreadData } from "@liveblocks/client"
import { TableContent } from "@prismicio/types-internal/lib/content"
import { Table as TTTable } from "@tiptap/extension-table"
import type { Node as ProseMirrorNode, Schema } from "@tiptap/pm/model"
import { EditorState, Plugin } from "@tiptap/pm/state"
import {
  addColumn,
  addRow,
  columnResizing,
  isInTable,
  selectedRect,
  tableEditing,
} from "@tiptap/pm/tables"
import { type CommandProps } from "@tiptap/react"

import { NodeUtils, TableExtension } from "../../models"
import { ExtendedRichTextNodeType, type TableBlock } from "../../types"
import {
  tableCellFocusPlugin as tableCellFocus,
  tableHandlesPlugin as tableHandles,
} from "./plugins"
import {
  TableCell as TableCellNode,
  TableHeader as TableHeaderNode,
} from "./TableCell"
import { TableEvent } from "./TableEvent"
import { TableRow as TableRowNode, tableRowConverter } from "./TableRow"
import { type TableOptions, TableView } from "./TableView"
import {
  getNodeFromSchema,
  isTableColumnLimitReached,
  isTableRowLimitReached,
  moveColumn,
  moveRow,
  selectColumn,
  selectRow,
} from "./utils"

type TableRowBlock = TableContent["content"][number]

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    Table: {
      /**
       * Add a row at the end of the table
       * @returns True if the command was successful, otherwise false
       * @example editor.commands.addRowToEnd()
       */
      addRowToEnd: () => ReturnType
      /**
       * Add a column at the end of the table
       * @returns True if the command was successful, otherwise false
       * @example editor.commands.addColumnToEnd()
       */
      addColumnToEnd: () => ReturnType
      /**
       * Move the selected row up or down
       * @returns True if the command was successful, otherwise false
       * @example editor.commands.moveRow("up")
       */
      moveRow: (direction: "up" | "down") => ReturnType
      /**
       * Move the selected column left or right
       * @returns True if the command was successful, otherwise false
       * @example editor.commands.moveColumn("left")
       */
      moveColumn: (direction: "left" | "right") => ReturnType
    }
  }
}

const TableNode = TTTable.extend<TableOptions>({
  name: "table",
  selectable: false,

  addOptions() {
    return {
      ...this.parent?.(),
      resizable: true,
      defaultCellMinWidth: 120,
      View: null,
    }
  },
  addCommands() {
    return {
      ...this.parent?.(),
      addRowAfter: () => {
        return (args) => {
          const { editor } = args
          if (isTableRowLimitReached(editor)) {
            TableEvent.dispatch("addAtLimit", {
              editor,
              type: "row",
            })
            return false
          }
          return this.parent?.().addRowAfter?.()(args) ?? false
        }
      },
      addRowBefore: () => {
        return (args) => {
          const { editor } = args
          if (isTableRowLimitReached(editor)) {
            TableEvent.dispatch("addAtLimit", {
              editor,
              type: "row",
            })
            return false
          }
          return this.parent?.().addRowBefore?.()(args) ?? false
        }
      },
      addRowToEnd: () => {
        return ({ dispatch, state, editor }: CommandProps) => {
          if (!isInTable(state)) return false
          if (isTableRowLimitReached(editor)) {
            TableEvent.dispatch("addAtLimit", {
              editor,
              type: "row",
            })
            return false
          }
          if (dispatch) {
            const rect = selectedRect(state)
            const rowCount = rect.map.height
            dispatch(addRow(state.tr, rect, rowCount))
          }
          return true
        }
      },
      addColumnAfter: () => {
        return (args) => {
          const { editor } = args
          if (isTableColumnLimitReached(editor)) {
            TableEvent.dispatch("addAtLimit", {
              editor,
              type: "column",
            })
            return false
          }
          return this.parent?.().addColumnAfter?.()(args) ?? false
        }
      },
      addColumnBefore: () => {
        return (args) => {
          const { editor } = args
          if (isTableColumnLimitReached(editor)) {
            TableEvent.dispatch("addAtLimit", {
              editor,
              type: "column",
            })
            return false
          }
          return this.parent?.().addColumnBefore?.()(args) ?? false
        }
      },
      addColumnToEnd: () => {
        return ({ dispatch, state, editor }: CommandProps) => {
          if (!isInTable(state)) return false
          if (isTableColumnLimitReached(editor)) {
            TableEvent.dispatch("addAtLimit", {
              editor,
              type: "column",
            })
            return false
          }
          if (dispatch) {
            const rect = selectedRect(state)
            const colCount = rect.map.width
            dispatch(addColumn(state.tr, rect, colCount))
          }
          return true
        }
      },
      moveRow: (direction: "up" | "down") => {
        return ({ dispatch, state }: CommandProps) => {
          if (!isInTable(state)) return false

          if (dispatch) {
            // Clear text selection because sometimes in production, when selecting
            // a row/column, the first cell text gets selected, and if the user moves
            // the row/column, the text selection stays in the previous position,
            // which is not the desired experience.
            clearWindowTextSelection()

            const moveResult = moveRow({ direction, state })
            if (!moveResult) return false

            // select the row after moving because it was recreated
            const moveAndSelectTransaction = selectRow(moveResult)
            dispatch(moveAndSelectTransaction)
          }

          return true
        }
      },
      moveColumn: (direction: "left" | "right") => {
        return ({ dispatch, state }: CommandProps) => {
          if (!isInTable(state)) return false

          if (dispatch) {
            const moveResult = moveColumn({ direction, state })
            if (!moveResult) return false

            // select the column after moving because it was recreated
            const moveAndSelectTransaction = selectColumn(moveResult)
            dispatch(moveAndSelectTransaction)
          }

          return true
        }
      },
    }
  },
  addNodeView() {
    return (props) => new TableView(props, this.options)
  },
  addKeyboardShortcuts() {
    return {
      ...this.parent?.(),
      // Override the default behavior of delete shortcuts.
      // https://github.com/ueberdosis/tiptap/blob/main/packages/extension-table/src/table.ts#L417
      Backspace: () => false,
      "Mod-Backspace": () => false,
      Delete: () => false,
      "Mod-Delete": () => false,
    }
  },
  addProseMirrorPlugins() {
    // We override the default table plugins because the original code passes
    // cellMinWidth to the defaultCellMinWidth argument when calling
    // columnResizing. This causes all the other columns without a fixed width
    // to be shrunk while resizing.
    // See: https://github.com/ueberdosis/tiptap/blob/3e59097b34ce8bc8c39e1def67eb31a1d9f9e5c2/packages/extension-table/src/table.ts#L434
    //
    // ⚠️ The order of the plugins seems to make a difference in the selection
    // behavior, so make sure to start with columnResizing > tableEditing.

    const isResizable = this.options.resizable && this.editor.isEditable
    return [
      ...(isResizable ? [columnResizing(this.options)] : []),
      tableEditing(this.options),
      tableHandles(this.editor),
      tableCellFocus(this.editor),
      tableDeletePrevention(),
    ]
  },
})

function clearWindowTextSelection() {
  if (!window.getSelection) return
  const selection = window.getSelection()
  if (selection?.empty) {
    selection.empty()
  } else if (selection?.removeAllRanges) {
    selection.removeAllRanges()
  }
}

interface ReplaceStepJSON {
  stepType: "replace" | "replaceAround"
  from: number
  to: number
}

function tableDeletePrevention() {
  return new Plugin({
    filterTransaction(transaction, state) {
      for (const step of transaction.steps) {
        const replaceStep = step.toJSON() as ReplaceStepJSON
        // make sure it's of type replace, otherwise other transactions will be
        // undesirably prevented (e.g. table resizing)
        if (replaceStep.stepType !== "replace") continue

        const fromNode = getNodeAt(state, replaceStep.from)
        if (!fromNode) continue

        // check if steps affect the table node or all the content of the
        // document
        if (
          fromNode.type.name === TableNode.name ||
          replaceStep.from + replaceStep.to === state.doc.content.size
        ) {
          return false // prevent the transaction
        }
      }
      return true // allow the transaction
    },
  })
}

function getNodeAt(state: EditorState, from: number) {
  try {
    return state.doc.nodeAt(from)
  } catch (error) {
    // This can fail, especially when adding a new column, most likely because
    // the node might not exist yet. It's not critical, as the action is working
    // correctly.
    return null
  }
}

const tableConverter = {
  fromPrismic(
    id: string,
    schema: Schema,
    threads?: ThreadData<BaseMetadata>[],
  ) {
    return function (table: TableBlock): ProseMirrorNode {
      const node = getNodeFromSchema(schema, TableNode.name)
      return node.create(
        {},
        table.content.map((row) =>
          tableRowConverter.fromPrismic(id, schema, threads)(row),
        ),
      )
    }
  },

  toPrismic(node: ProseMirrorNode): TableBlock {
    // If the table has controls, the first node will be the wrapper with the controls.
    const firstTableNode =
      node.content.firstChild?.type.name === TableNode.name
        ? node.content.firstChild
        : node

    return {
      type: "table",
      content: NodeUtils.children.map<TableRowBlock, false>(
        firstTableNode,
        (row) => tableRowConverter.toPrismic(row),
      ),
    }
  },
}

export const Table = new TableExtension(
  [ExtendedRichTextNodeType.table],
  ExtendedRichTextNodeType.table,
  [TableNode, TableRowNode, TableHeaderNode, TableCellNode],
  tableConverter,
)
